import { type FC } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { getEmptyAddress } from "@jti/commerce";
import { RadioButton, RadioButtonGroup } from "@jti/core-components";
import { isAddressFormExtendedWithProvince } from "@jti/fe-common";
import { useSelector } from "@jti/store/reduxHooks";
import type { BillingAddressType } from "@jti/store/types";
import { Controller, useFormContext, useTranslations } from "@jti/ui";

import { SHIPPING_FORM_FIELDS } from "@checkout/forms/ShippingForm/constants";

import BillingAddressEditForm from "../BillingAddressEditForm";

import styles from "../BillingAddress.module.scss";

type BillingAddressAtHomeDeliveryProps = {
  value: BillingAddressType;
};

export const BillingAddressAtHomeDelivery: FC<BillingAddressAtHomeDeliveryProps> = ({ value }) => {
  const { __ } = useTranslations();
  const { control, getValues, setValue } = useFormContext();
  const { addressFormVariant } = useConfig();
  const isAddressExtendedWithProvince = isAddressFormExtendedWithProvince(addressFormVariant);

  const country = useSelector((state) => state.checkout.country);

  const billingOptions = [
    { value: "shipping", label: __("cart.billing.option.address.use_shipping") },
    { value: "custom", label: __("cart.billing.option.address.use_different") },
  ];

  const handleBillingAddressTypeChange = (newBillingAddressType: string) => {
    if (newBillingAddressType === "custom") {
      const currentBillingAddress = getValues(SHIPPING_FORM_FIELDS.billingAddress);

      setValue(
        SHIPPING_FORM_FIELDS.billingAddress,
        getEmptyAddress({
          currentAddress: currentBillingAddress,
          overrides: {
            countryCode: country.countryCode,
            countryName: country.countryName,
            region: isAddressExtendedWithProvince ? currentBillingAddress?.region : undefined,
            regionCode: isAddressExtendedWithProvince ? currentBillingAddress?.regionCode : undefined,
          },
        })
      );
    }
  };

  return (
    <>
      <p className={styles.intro}>{__("cart.billing.subtitle")}</p>
      <Controller
        name={SHIPPING_FORM_FIELDS.billingAddressType}
        control={control}
        rules={{
          required: __("validation.field_required"),
        }}
        render={({ field }) => (
          <RadioButtonGroup name={field.name} orientation="vertical">
            {billingOptions.map((opt) => (
              <RadioButton
                key={opt.value}
                value={opt.value}
                label={opt.label}
                checked={value === opt.value}
                onChange={(event) => {
                  const nextValue = event.currentTarget.value;
                  field.onChange(nextValue);
                  handleBillingAddressTypeChange(nextValue);
                }}
              />
            ))}
          </RadioButtonGroup>
        )}
      />
      {value === "custom" && <BillingAddressEditForm />}
    </>
  );
};
